import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BarChart3, BookOpen, CheckCircle2, ClipboardList, Download, Eye,
  FileText, Layers, LayoutDashboard, Mail, Plus, Sparkles, Trash2, Users,
} from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import ActivityFeed from "@/components/admin/ActivityFeed";
import TranslationBadge from "@/components/admin/TranslationBadge";
import ContentInbox from "@/components/admin/ContentInbox";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/dashboard — Command Center home.
 *
 * Sections (v3):
 *   1. Welcome + Epictetus quote
 *   2. Quick Actions
 *   3. Daily Command Center — 8 metric tiles
 *   4. Recent articles — 5 latest, inline actions (Edit/Publish/Preview/Delete)
 *   5. Recent activity — timeline pulled from /api/admin/activity
 */

function QuickAction({ to, icon: Icon, label, testId, primary = false }) {
  const base =
    "inline-flex items-center gap-2 px-4 py-2.5 text-[10px] md:text-[11px] tracking-[0.28em] uppercase transition-colors";
  const styles = primary
    ? "bg-gold text-ink hover:bg-gold-hover"
    : "border border-gold/40 text-gold hover:bg-gold/10";
  return (
    <Link to={to} data-testid={testId} className={`${base} ${styles}`}>
      <Icon size={13} strokeWidth={1.75} /> {label}
    </Link>
  );
}

function fmt(n) {
  if (n === undefined || n === null) return "—";
  return typeof n === "number" ? n.toLocaleString() : String(n);
}

function RecentArticleRow({ doc, onPublish, onDelete, busy, t }) {
  const title = (doc.title && (doc.title.en || doc.title.nl)) || doc.slug;
  const published = doc.status === "published";
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-b-0">
      <div className="min-w-0">
        <p className="font-serif text-[15px] text-bone truncate">{title}</p>
        <div className="mt-1 flex items-center flex-wrap gap-1.5">
          <span className="text-[10px] tracking-[0.24em] uppercase text-bone/50">
            {doc.category} · {published ? t("admin.label.published") : t("admin.label.draft")}
            {doc.source === "autoseo" && " · AutoSEO"}
          </span>
          <TranslationBadge article={doc} />
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Link
          to={`/admin/journal?edit=${doc.id}`}
          data-testid={`row-edit-${doc.slug}`}
          title={t("admin.action.edit")}
          className="p-2 text-bone/70 hover:text-gold border border-transparent hover:border-gold/30"
        >
          <FileText size={13} strokeWidth={1.5} />
        </Link>
        {published ? (
          <a
            href={`/stoic-journal/${doc.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`row-preview-${doc.slug}`}
            title={t("admin.action.preview")}
            className="p-2 text-bone/70 hover:text-gold border border-transparent hover:border-gold/30"
          >
            <Eye size={13} strokeWidth={1.5} />
          </a>
        ) : (
          <button
            type="button"
            onClick={() => onPublish(doc)}
            disabled={busy}
            data-testid={`row-publish-${doc.slug}`}
            title={t("admin.action.publish")}
            className="p-2 text-bone/70 hover:text-emerald-400 border border-transparent hover:border-emerald-400/30 disabled:opacity-40"
          >
            <CheckCircle2 size={13} strokeWidth={1.5} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(doc)}
          disabled={busy}
          data-testid={`row-delete-${doc.slug}`}
          title={t("admin.action.delete")}
          className="p-2 text-bone/70 hover:text-red-400 border border-transparent hover:border-red-400/30 disabled:opacity-40"
        >
          <Trash2 size={13} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useT();
  const [summary, setSummary] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [s, a] = await Promise.all([adminApi.summary(), adminApi.listArticles()]);
      setSummary(s);
      setArticles(a.articles || []);
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const recent = useMemo(() => (articles || []).slice(0, 5), [articles]);

  const doPublish = async (doc) => {
    setBusy(true);
    try { await adminApi.publishArticle(doc.id); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const doDelete = async (doc) => {
    const title = (doc.title && doc.title.en) || doc.slug;
    if (!window.confirm(t("admin.confirm.delete", { title }))) return;
    setBusy(true);
    try { await adminApi.deleteArticle(doc.id); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const a = summary?.articles || {};
  const m = summary?.messages || {};
  const w = summary?.waitlist || {};
  const seo = summary?.autoseo || {};

  return (
    <AdminShell>
      {/* Welcome */}
      <header className="mb-10 md:mb-12" data-testid="admin-welcome-header">
        <h1 className="font-serif text-3xl md:text-5xl text-bone leading-[1.1]" data-testid="admin-welcome-title">
          {t("admin.dash.welcome")}
        </h1>
        <p className="mt-4 text-sm md:text-base text-bone/60 tracking-wide" data-testid="admin-welcome-sub">
          {t("admin.dash.welcomeSub")}
        </p>
        <figure className="mt-8 border-l-2 border-gold/50 pl-5 md:pl-6 max-w-2xl" data-testid="admin-welcome-quote">
          <blockquote className="font-serif italic text-bone/85 text-[15px] md:text-lg leading-relaxed">
            &ldquo;First say to yourself what you would be; and then do what you have to do.&rdquo;
          </blockquote>
          <figcaption className="mt-3 text-[10px] tracking-[0.32em] uppercase text-gold/80">Epictetus</figcaption>
        </figure>
      </header>

      {/* Quick Actions */}
      <section className="mb-14" data-testid="admin-quick-actions">
        <p className="text-[10px] tracking-[0.32em] uppercase text-gold/70 mb-4">{t("admin.dash.quickActions")}</p>
        <div className="flex flex-wrap gap-3">
          <QuickAction to="/admin/editorial" icon={ClipboardList} label={t("admin.dash.openEditorial")} testId="qa-open-editorial" primary />
          <QuickAction to="/admin/journal" icon={Plus} label={t("admin.dash.newArticle")} testId="qa-new-article" />
          <QuickAction to="/admin/journal" icon={BookOpen} label={t("admin.dash.openJournal")} testId="qa-open-journal" />
          <QuickAction to="/admin/dashboard" icon={LayoutDashboard} label={t("admin.dash.openDashboard")} testId="qa-open-dashboard" />
        </div>
      </section>

      {/* Daily Command Center */}
      <section className="mb-14" data-testid="daily-command-center">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-serif text-2xl md:text-3xl text-bone">{t("admin.dash.daily")}</h2>
          <p className="text-[10px] tracking-[0.28em] uppercase text-bone/40">
            {t("admin.label.live")} · {summary?.generated_at ? new Date(summary.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          <StatCard testId="stat-drafts"    icon={FileText}     label={t("admin.dash.stat.drafts")}     value={fmt(a.drafts)}    hint={t("admin.dash.stat.drafts.hint")}    loading={loading} />
          <StatCard testId="stat-published" icon={CheckCircle2} label={t("admin.dash.stat.published")}  value={fmt(a.published)} hint={t("admin.dash.stat.published.hint")} accent="green" loading={loading} />
          <StatCard testId="stat-total"     icon={Layers}       label={t("admin.dash.stat.total")}      value={fmt(a.total)}     hint={t("admin.dash.stat.total.hint")}     loading={loading} />
          <StatCard testId="stat-messages"  icon={Mail}         label={t("admin.dash.stat.messages")}   value={fmt(m.last_7d)}   hint={`${fmt(m.total)} ${t("admin.label.lifetime")}`} loading={loading} />
          <StatCard testId="stat-visitors"  icon={Users}        label={t("admin.dash.stat.visitors")}   value="—"                hint={t("admin.dash.stat.visitors.hint")}  accent="fog" />
          <StatCard testId="stat-downloads" icon={Download}     label={t("admin.dash.stat.downloads")}  value="—"                hint={t("admin.label.placeholder")}         accent="fog" />
          <StatCard
            testId="stat-autoseo"
            icon={Sparkles}
            label={t("admin.dash.stat.autoseo")}
            value={seo.enabled ? t("admin.label.connected") : t("admin.label.off")}
            hint={seo.last_import_at
              ? t("admin.dash.stat.autoseo.last", { date: new Date(seo.last_import_at).toLocaleDateString() })
              : t("admin.dash.stat.autoseo.noImports")}
            accent={seo.enabled ? "green" : "red"}
            loading={loading}
          />
          <StatCard testId="stat-system"    icon={BarChart3}    label={t("admin.dash.stat.system")}     value={t("admin.label.nominal")} hint={t("admin.dash.stat.system.hint", { count: fmt(w.total) })} accent="green" loading={loading} />
        </div>
        {err && <p className="mt-4 text-sm text-red-400" data-testid="stats-error">{err}</p>}
      </section>

      {/* Content Inbox — everything that requires attention today */}
      <ContentInbox />

      {/* Two-column: Recent articles + Activity feed */}
      <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 mb-14">
        {/* Recent articles */}
        <div>
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-serif text-2xl md:text-3xl text-bone">{t("admin.label.recentArticles")}</h2>
            <Link
              to="/admin/journal"
              data-testid="view-all-articles"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold hover:text-bone transition-colors"
            >
              {t("admin.action.viewAllArticles")} <ArrowRight size={12} strokeWidth={1.75} />
            </Link>
          </div>
          <div className="border border-gold/15 bg-white/[0.02] px-5 md:px-6" data-testid="recent-articles-list">
            {loading && <p className="py-5 text-sm text-bone/50">{t("admin.label.loading")}</p>}
            {!loading && recent.length === 0 && (
              <p className="py-5 text-sm text-bone/50">{t("admin.dash.noArticles")}</p>
            )}
            {recent.map((doc) => (
              <RecentArticleRow
                key={doc.id}
                doc={doc}
                onPublish={doPublish}
                onDelete={doDelete}
                busy={busy}
                t={t}
              />
            ))}
          </div>
        </div>

        {/* Activity feed */}
        <div>
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-serif text-2xl md:text-3xl text-bone">{t("admin.label.recentActivity")}</h2>
          </div>
          <div className="border border-gold/15 bg-white/[0.02] px-5 md:px-6 min-h-[200px]">
            <ActivityFeed limit={10} />
          </div>
        </div>
      </section>
    </AdminShell>
  );
}
